import { AuthRoleIdEnum, AuthRoleNameEnum } from 'constants/user'
import { EMembershipStatus } from 'constants/membership'
import { EOrganizationType } from 'constants/organization-type'
import { IGroupMember } from 'interfaces/group'
import { IOrganization } from 'interfaces/organization'
import { BillingStatus } from 'types/billing'

export declare interface ILoginRequest {
  username?: string
  email?: string
  password: string
}

/** Membership info returned from login response — used for org picker + switcher */
export interface MembershipInfo {
  orgId: number
  orgName: string
  orgLogo?: string
  /** Organization.primaryColor — paints the dashboard banner. Unset on older rows. */
  orgPrimaryColor?: string
  orgSubdomain?: string
  /**
   * Organization.organizationType — the source of this Organization's Class and
   * Team Level option lists. Carried per membership so switching organization
   * swaps the lists with it. Unset on Organizations predating the field.
   */
  orgType?: EOrganizationType
  roleId: AuthRoleIdEnum
  billingStatus: BillingStatus
  // Additive, optional — only the Teams view (GET /users/me/memberships) populates
  // these. `status === PENDING` marks an invite-only row (no joinedAt/memberCount).
  status?: EMembershipStatus
  joinedAt?: string
  memberCount?: number
  /** Sports across the Organization's live Teams. */
  sports?: string[]
  /** The member's Class slug in this Organization (see constants/player-class). */
  playerClass?: string | null
  /** Organization-specific label shown when Class is `other`. */
  playerClassCustomLabel?: string | null
}

/** Server-side filter for the Teams management list (GET /users/me/teams). */
export type TeamMembershipFilter = 'owned' | 'invited' | 'invite-pending'

/**
 * Row from GET /users/me/teams (Teams management list). Richer than MembershipInfo:
 * carries `membershipId` (for leave) and `invitationToken` (accept/decline on
 * invite-pending rows). Kept off the auth memberships endpoint.
 */
export interface TeamListItem {
  /** Parent Organization — the account the Team belongs to. */
  orgId: number
  orgName: string
  orgLogo?: string
  /** Organization.primaryColor — paints the dashboard banner. Unset on older rows. */
  orgPrimaryColor?: string
  /**
   * The seasonal Team this row represents. One Organization may hold several
   * Teams, so a membership can produce several rows. Absent on invite-pending
   * rows and on Organizations with no Team yet.
   */
  teamId?: number
  teamName?: string
  teamImage?: string
  /** Season descriptors, sourced from the Team. */
  sports?: string[]
  season?: string
  year?: number
  ageLevel?: string
  membershipId?: number
  invitationToken?: string
  roleId: AuthRoleIdEnum
  status: EMembershipStatus
  joinedAt?: string
  memberCount?: number
}

/**
 * Global user profile — no org-specific fields.
 * Org context (role, orgId) comes from MembershipInfo via activeOrgId.
 * Legacy fields (organizationId, authRoleId, authRole) kept optional for backward compat during migration.
 */
export interface AuthUserProfile {
  id: number
  username?: string
  email?: string
  isEmailVerified?: boolean
  ssoEmail?: string
  /** @deprecated Use currentMembership from authStore instead */
  authRoleId?: AuthRoleIdEnum
  /** @deprecated Use currentMembership from authStore instead */
  authRole?: AuthRoleNameEnum
  isEditor?: boolean
  name?: string
  firstName?: string
  lastName?: string
  /** @deprecated Use authStore.activeOrgId instead */
  organizationId?: number
  image?: string
  disabled?: boolean
  groupMembers: IGroupMember[]
  isReportTool?: boolean
  isMessageFullAccess?: boolean
  isResetPassword?: boolean
  resetPasswordToken?: string
  canCreateCollections?: boolean
  ssoEmailVerified?: boolean
  needBindEmail?: boolean
  hasLocalPassword?: boolean
  googleLinked?: boolean
  registeredWithGoogle?: boolean
}

export interface ILoginResponse {
  userInfo: AuthUserProfile
  token: string
  memberships: MembershipInfo[]
}

export interface ISignUpRequest {
  username?: string
  email?: string
  firstName: string
  lastName: string
  password: string
  authRoleId?: number
}

export interface ICreateOrganizationAndUserRequest {
  userData: ISignUpRequest
  organization: IOrganization
}

export interface IForgotResetPassDto {
  resetToken: string
  newPassword: string
}

export enum BasicRoleEnum {
  ADMIN = 1,
  MANAGER = 2,
  NORMAL = 3
}

export interface IChangePasswordDTO {
  username?: string
  email?: string
  authorId?: number
  oldPassword?: string
  newPassword: string
}

export interface IAdminChangePasswordDTO {
  userId?: number
  newPassword: string
}

/** Google SSO — returned when credential is valid but user has no account yet */
export interface IGoogleNeedsRegistration {
  needsRegistration: true
  googleProfile: { email: string; firstName: string; lastName: string; picture?: string }
}

export function isGoogleNeedsRegistration(
  response: ILoginResponse | IGoogleNeedsRegistration
): response is IGoogleNeedsRegistration {
  return 'needsRegistration' in response && response.needsRegistration === true
}

export type AccountRole = 'athlete' | 'coach' | 'guardian'

/** Standalone registration — no org required */
export interface IRegisterRequest {
  email: string
  firstName: string
  lastName: string
  password: string
  accountType: AccountRole
  /** From `?invitation=` on the invite email link — auto-joins the org at register time. */
  invitationToken?: string
}
